import { useState, useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import NavigationDropdown from "../../components/NavigationDropdown";
import NavigationPanel from "../../components/NavigationPanel";
import PortalPopup from "../../components/PortalPopup";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import ExportModalImage from "../../components/ExportModalImage";
import Breadcrumb from "../../components/Breadcrumb";
import CTAs from "../../components/CTAs";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import PoweredByAITag from "../../components/PoweredByAITag";
import Footer from "../../components/Footer";
import { Flag } from "lucide-react";
import { Flag2 } from "iconsax-reactjs";

const Defence_3 = () => {
  const navigate = useNavigate();
  const [isNavigationDropdownOpen, setNavigationDropdownOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);
  const [isExportModalImageOpen, setExportModalImageOpen] = useState(false);

  const closeNavigationDropdown = useCallback(() => {
    setNavigationDropdownOpen(false);
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const openExportModalImage = useCallback(() => {
    setExportModalImageOpen(true);
  }, []);

  const closeExportModalImage = useCallback(() => {
    setExportModalImageOpen(false);
  }, []);

  const onTabStatesContainerClick = useCallback(() => {
    navigate("/defence2");
  }, [navigate]);

  const onClickNegotiation = useCallback(() => {
    navigate("/defence4");
  }, [navigate]);

  const breadcrumb = [
    { label: "Defence and Case Handling", path: "/defence1", active: false },
    { label: "Paras Mistry", path: "", active: true },
  ];

  const COLORS = ["#335869", "#487C94", "#84A7B7"]; // Active segment colors
  const BG_COLOR = "#EDF2F4"; // Background color for empty space

  const [data, setData] = useState([
    { name: "Overall Accuracy", value: 85 },
    { name: "False Positive", value: 45 },
    { name: "True Positive", value: 67 },
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Replace with API endpoint
        const response = await fetch("/api/realtime-data");
        const result = await response.json();
        setData([
          { name: "Overall Accuracy", value: result.overallAccuracy },
          { name: "False Positive", value: result.falsePositive },
          { name: "True Positive", value: result.truePositive },
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, []);

  // Function to get remaining value for the background
  const getRemainingValue = (value) => 100 - value;

  return (
    <>
      <div className="w-full relative min-h-screen overflow-visible bg-neutrals-neutral-50 text-left text-lg text-neutrals-neutral-900 font-open-sans-ff-heading-3">
        {/* Navigation Panel */}
        <NavigationPanel />

        {/* Main */}
        <div className="w-full absolute top-[136px] flex flex-col items-start justify-start gap-6 px-4 md:px-3 lg:px-24 box-border">
          {/* Breadcrumb Navigation */}
          <Breadcrumb breadcrumbs={breadcrumb} />

          {/* Heading */}
          <div className="self-stretch bg-white border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-col items-start justify-start pt-0 px-0 pb-6 text-9xl text-neutrals-neutral-900">
            <div className="self-stretch flex flex-row items-start justify-start">
              <div className="flex-1 flex flex-col items-start justify-start gap-1.5">
                <b className="self-stretch relative text-9xl">
                  Defence and Case Handling
                </b>
                <div className="self-stretch relative text-base text-neutrals-neutral-300">
                  Acknowledge defences and revert back.
                </div>
              </div>
            </div>
          </div>

          <div className="self-stretch flex flex-col items-start justify-start gap-6 text-base">
            <div className="flex flex-row items-start justify-start gap-6">
              <div
                className="bg-white h-[41px] flex flex-row items-center justify-center pt-0 px-1 pb-3 box-border cursor-pointer"
                onClick={onTabStatesContainerClick}
              >
                <div className="flex flex-row items-center justify-center">
                  <div className="relative font-semibold">Case details</div>
                </div>
              </div>
              <div className="bg-white border-primary-primary-500 border-solid border-b-[2px] box-border h-[41px] flex flex-row items-center justify-center pt-0 px-1 pb-3 text-primary-primary-500">
                <div className="flex flex-row items-center justify-center">
                  <div className="relative font-semibold">AI utilisation</div>
                </div>
              </div>
              <div
                className="bg-white h-[41px] flex flex-row items-center justify-center pt-0 px-1 pb-3 box-border cursor-pointer"
                onClick={onClickNegotiation}
              >
                <div className="flex flex-row items-center justify-center">
                  <div className="relative font-semibold">Negotiation</div>
                </div>
              </div>
            </div>

            <div className="self-stretch flex flex-col items-start justify-start gap-8 text-lg text-neutrals-neutral-900">
              {/* Alert Panel */}
              <div className="w-full flex flex-col items-start justify-between gap-[24px]">
                <div className="w-full relative border-neutrals-neutral-100 border-solid border-b-[1px] box-border flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-0 text-left text-lg text-neutrals-neutral-900 font-open-sans-font-family-meta-strong">
                  <div className="flex flex-col items-start justify-start gap-1.5">
                    <div className="relative font-semibold">Alert panel</div>
                    <div className="relative text-base text-neutrals-neutral-300">
                      Acknowledges any formal defence raised by the debtor.
                    </div>
                  </div>
                  <PoweredByAITag />
                </div>

                {/* Blocks */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-start justify-start gap-6">
                  <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-col items-start justify-start border-[1px] border-solid border-neutrals-neutral-100">
                    <div className="w-full flex flex-col items-start justify-start p-6 box-border gap-6">
                      <div className="self-stretch flex flex-row items-start justify-between">
                        <div className="relative font-semibold">
                          Potential Dispute
                        </div>
                        <Flag2 size={20} variant="Bold" color="#D93C3D" />
                      </div>
                      <div className="self-stretch flex flex-col items-start justify-start gap-[10px] text-sm">
                        <div className="flex flex-row items-center justify-start gap-[10px]">
                          <img
                            className="w-10 relative h-10"
                            alt=""
                            src="assets/pdf.svg"
                          />
                          <div className="relative font-semibold">
                            DefenseStatement_JDoe.pdf
                          </div>
                        </div>
                        <div className="self-stretch relative text-base text-neutrals-neutral-300">
                          AI has identified language patterns suggesting a
                          possible dispute.
                        </div>
                      </div>
                    </div>
                    <div className="w-full box-border flex flex-col items-end justify-start py-4 px-6 text-sm border-t-[1px] border-solid border-neutrals-neutral-100">
                      <CTAs
                        type="Secondarybtn"
                        text="View document"
                        borderColor="border-neutrals-neutral-100"
                        position="relative"
                      />
                    </div>
                  </div>
                  <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-col items-start justify-start border-[1px] border-solid border-neutrals-neutral-100">
                    <div className="w-full flex flex-col items-start justify-start p-6 box-border gap-6">
                      <div className="self-stretch flex flex-row items-start justify-between">
                        <div className="relative font-semibold">
                          Missing Information
                        </div>
                        <Flag2 size={20} variant="Bold" color="#D93C3D" />
                      </div>
                      <div className="self-stretch flex flex-col items-start justify-start gap-[10px] text-sm">
                        <div className="flex flex-row items-center justify-start gap-[10px]">
                          <div className="flex flex-row items-start justify-start relative gap-[10px]">
                            <img
                              className="w-10 relative h-10 z-[0]"
                              alt=""
                              src="assets/image_file.svg"
                            />
                          </div>
                          <div className="relative font-semibold">
                            Correspondence_JDoe.png
                          </div>
                        </div>
                        <div className="self-stretch relative text-base text-neutrals-neutral-300">
                          Key information appears to be missing or obscured.
                        </div>
                      </div>
                    </div>
                    <div className="w-full box-border flex flex-col items-end justify-start py-4 px-6 text-sm border-t-[1px] border-solid border-neutrals-neutral-100">
                      <CTAs
                        type="Secondarybtn"
                        text="View document"
                        borderColor="border-neutrals-neutral-100"
                        position="relative"
                      />
                    </div>
                  </div>
                  <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 flex flex-col items-start justify-start border-[1px] border-solid border-neutrals-neutral-100">
                    <div className="w-full flex flex-col items-start justify-start p-6 box-border gap-[24px]">
                      <div className="self-stretch flex flex-row items-start justify-between">
                        <div className="relative font-semibold">
                          Payment Intent Recognised
                        </div>
                        <Flag2 size={20} variant="Bold" color="#5FCF6B" />
                      </div>
                      <div className="self-stretch flex flex-col items-start justify-start gap-[10px] text-sm">
                        <div className="flex flex-row items-center justify-start gap-[10px]">
                          <div className="flex flex-row items-start justify-start relative gap-[10px]">
                            <img
                              className="w-10 relative h-10 z-[0]"
                              alt=""
                              src="assets/image_file.svg"
                            />
                          </div>
                          <div className="relative font-semibold">
                            Correspondence_JDoe.png
                          </div>
                        </div>
                        <div className="self-stretch relative text-base text-neutrals-neutral-300">
                          AI detects language indicating a willingness to
                          settle.
                        </div>
                      </div>
                    </div>
                    <div className="w-full box-border flex flex-col items-end justify-start py-4 px-6 text-sm border-t-[1px] border-solid border-neutrals-neutral-100">
                      <CTAs
                        type="Secondarybtn"
                        text="View document"
                        borderColor="border-neutrals-neutral-100"
                        position="relative"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Insights */}
              <div className="w-full flex flex-col items-start justify-start gap-6 text-lg text-neutrals-neutral-900">
                <div className="w-full relative border-neutrals-neutral-100 border-solid border-b-[1px] box-border flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-0 text-left text-lg text-neutrals-neutral-900 font-open-sans-font-family-meta-strong">
                  <div className="flex flex-col items-start justify-start gap-1.5">
                    <div className="relative font-semibold">AI Insights</div>
                    <div className="relative text-base text-neutrals-neutral-300">
                      Acknowledges any formal defence raised by the debtor.
                    </div>
                  </div>
                  <PoweredByAITag />
                </div>

                {/* Bar Chart */}
                <div className="self-stretch flex flex-row items-start justify-start gap-6">
                  <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-neutrals-neutral-50 border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-6">
                    <div className="self-stretch flex flex-row items-center justify-between gap-0">
                      <div className="relative font-semibold">
                        AI identified issues over time
                      </div>
                      <div
                        className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-wireframes-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2.5 px-3.5 cursor-pointer text-sm"
                        onClick={openExportModalImage}
                      >
                        <div className="flex flex-row items-center justify-center gap-1.5">
                          <img
                            className="w-5 relative h-5"
                            alt=""
                            src="assets/export.svg"
                          />
                          <div className="relative font-semibold">Export</div>
                        </div>
                      </div>
                    </div>
                    <div className="self-stretch flex flex-col items-start justify-start text-sm text-neutrals-neutral-300">
                      <div className="self-stretch flex flex-col items-end justify-start gap-6">
                        <div className="flex flex-row items-start justify-start gap-2.5">
                          <div className="flex flex-row items-center justify-start gap-1.5">
                            <div className="w-2 relative rounded-[50%] bg-primary-primary-500 h-2" />
                            <div className="relative">Green flag</div>
                          </div>
                          <div className="flex flex-row items-center justify-start gap-1.5">
                            <div className="w-2 relative rounded-[50%] bg-primary-primary-300 h-2" />
                            <div className="relative">Red flag</div>
                          </div>
                        </div>

                        {/* Graph area */}
                        <div className="self-stretch flex flex-row items-center justify-start gap-2.5 text-center text-neutrals-neutral-900">
                          <div className="flex flex-row items-center justify-start">
                            <div className="flex flex-row items-center justify-start gap-2.5">
                              <div className="relative font-semibold [transform:_rotate(-90deg)]">
                                Flags
                              </div>
                              <div className="h-[201px] flex flex-col items-end justify-start gap-[11px] text-left text-neutrals-neutral-300">
                                <div className="relative">60</div>
                                <div className="relative">50</div>
                                <div className="relative">40</div>
                                <div className="relative">30</div>
                                <div className="relative">20</div>
                                <div className="relative">10</div>
                                <div className="relative">0</div>
                              </div>
                            </div>
                          </div>

                          <div className="flex-1 flex flex-col items-start justify-start relative gap-2.5">
                            <div className="self-stretch relative h-[180px] z-[0]">
                              <div className="absolute top-[-0.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[29.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[59.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[89.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[119.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[149.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                              <div className="absolute top-[179.5px] left-[-0.5px] border-neutrals-neutral-100 border-solid border-t-[1px] box-border w-full h-px" />
                            </div>
                            <img
                              className="w-full absolute !m-[0] top-[12px] left-[0px] h-[168px] z-[1]"
                              alt=""
                              src="Group 233.svg"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="self-stretch h-12 flex flex-col items-start justify-between gap-0 text-center">
                        <div className="self-stretch flex flex-row items-start justify-between py-0 pl-14 pr-0 gap-0">
                          <div className="relative">Mon</div>
                          <div className="relative">Tue</div>
                          <div className="relative">Wed</div>
                          <div className="relative">Thu</div>
                          <div className="relative">Fri</div>
                          <div className="relative">Sat</div>
                          <div className="relative">Sun</div>
                        </div>
                        <div className="self-stretch relative font-semibold text-neutrals-neutral-900">
                          Days
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pie Chart */}
                  <div className="self-stretch max-w-[calc(100%-192px)] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-6">
                    <div className="self-stretch bg-wireframes-white border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-center justify-between pt-0 px-0 pb-6 gap-0">
                      <div className="relative font-semibold">Overview</div>
                      <img
                        className="w-5 relative h-5 overflow-hidden shrink-0"
                        alt=""
                        src="assets/more-vert.svg"
                      />
                    </div>

                    <div className="self-stretch flex flex-row items-start justify-start gap-6 text-right text-sm text-neutrals-neutral-300">
                      <div className="w-60 h-60 flex flex-row items-center justify-center">
                        <ResponsiveContainer>
                          <PieChart
                            width={234}
                            height={234}
                            viewBox="0 0 234 234"
                          >
                            {/* Base Background Ring */}
                            <Pie
                              data={[{ value: 100 }]}
                              cx="50%"
                              cy="50%"
                              innerRadius={80}
                              outerRadius={100}
                              fill={BG_COLOR}
                              dataKey="value"
                              stroke="none"
                            />

                            {/* Largest Ring - Overall Accuracy */}
                            <Pie
                              data={[
                                { value: data[0].value },
                                { value: getRemainingValue(data[0].value) },
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={80}
                              outerRadius={100}
                              dataKey="value"
                              stroke="none"
                              paddingAngle={3}
                              cornerRadius={100}
                              startAngle={90}
                            >
                              <Cell fill={COLORS[0]} />
                              <Cell fill="transparent" />
                            </Pie>

                            {/* Base Background Ring for Middle Ring */}
                            <Pie
                              data={[{ value: 100 }]}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={75}
                              fill={BG_COLOR}
                              dataKey="value"
                              stroke="none"
                            />

                            {/* Middle Ring - False Positive */}
                            <Pie
                              data={[
                                { value: data[1].value },
                                { value: getRemainingValue(data[1].value) },
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={55}
                              outerRadius={75}
                              dataKey="value"
                              stroke="none"
                              paddingAngle={3}
                              cornerRadius={100}
                            >
                              <Cell fill={COLORS[1]} />
                              <Cell fill={BG_COLOR} />
                            </Pie>

                            {/* Base Background Ring for Smallest Ring */}
                            <Pie
                              data={[{ value: 100 }]}
                              cx="50%"
                              cy="50%"
                              innerRadius={30}
                              outerRadius={50}
                              fill={BG_COLOR}
                              dataKey="value"
                              stroke="none"
                            />

                            {/* Smallest Ring - True Positive */}
                            <Pie
                              data={[
                                { value: data[2].value },
                                { value: getRemainingValue(data[2].value) },
                              ]}
                              cx="50%"
                              cy="50%"
                              innerRadius={30}
                              outerRadius={50}
                              dataKey="value"
                              stroke="none"
                              paddingAngle={3}
                              cornerRadius={100}
                            >
                              <Cell fill={COLORS[2]} />
                              <Cell fill={BG_COLOR} />
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Legend */}
                      <div className="flex flex-col gap-6 items-start">
                        {data.map((item, index) => (
                          <div key={index} className="flex items-start gap-2">
                            <div
                              className="w-2 h-2 rounded-full mt-1"
                              style={{ backgroundColor: COLORS[index] }}
                            />
                            <div className="flex flex-col items-start">
                              <span className="text-sm text-gray-600">
                                {item.name}
                              </span>
                              <span className="text-2xl font-semibold">
                                {item.value}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      {isNavigationDropdownOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeNavigationDropdown}
        >
          <NavigationDropdown onClose={closeNavigationDropdown} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
      {isExportModalImageOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeExportModalImage}
        >
          <ExportModalImage onClose={closeExportModalImage} />
        </PortalPopup>
      )}
    </>
  );
};

export default Defence_3;
